import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BiCheck, BiChevronLeft } from "react-icons/bi";
import cuid from "cuid";
import { defaultsDeep } from "lodash";
import Header from "shared/components/Header";
import Tabs from "shared/components/Tab";
import { NAME_SPACES } from "shared/locales/constants";
import { PROCESS_CONFIG_TYPES, PROCESS_TYPES } from "utils/constants";
import { serializeProcessProducts } from "../productConfig";
import { sortProcessStatuses } from "../sortProcessStatuses";
import {
  AdditionalConfiguration,
  General,
  Needs,
  PossibleStatuses,
  Skills,
  Statuses,
} from "./tabs";

const TABS = {
  GENERAL: "general",
  STATUSES: "statuses",
  POSSIBLE_STATUSES: "possibleStatuses",
  SKILLS: "skills",
  NEEDS: "needs",
  ADDITIONAL_CONFIG: "additionalConfig",
};

const Create = ({ onSubmit, cancel, id }) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PROCESS);
  const [modal, setModal] = useState({
    visible: false,
  });
  const [activeTab, setActiveTab] = useState(TABS.GENERAL);
  const generalFormRef = useRef(null);
  const statusesFormRef = useRef(null);
  const possibleStatusesFormRef = useRef(null);
  const skillsFormRef = useRef(null);
  const needsFormRef = useRef(null);
  const additionalConfigFormRef = useRef(null);
  const [isDirty, setIsDirty] = useState({
    [TABS.GENERAL]: false,
    [TABS.STATUSES]: false,
    [TABS.POSSIBLE_STATUSES]: false,
    [TABS.SKILLS]: false,
    [TABS.NEEDS]: false,
    [TABS.ADDITIONAL_CONFIG]: false,
  });
  const generalDirtyRef = useRef(false);
  const statusesDirtyRef = useRef(false);
  const possibleStatusesDirtyRef = useRef(false);
  const skillsDirtyRef = useRef(false);
  const needsDirtyRef = useRef(false);
  const additionalConfigDirtyRef = useRef(false);

  // Shared state for processStatuses (synced between Statuses and PossibleStatuses tabs)
  const [sharedProcessStatuses, setSharedProcessStatuses] = useState([]);

  const CREATE = t("CREATE", { returnObjects: true });
  const FORM = t("FORM", { returnObjects: true });

  const tabsConfig = [
    { key: TABS.GENERAL, label: FORM.GENERAL_INFO },
    { key: TABS.STATUSES, label: FORM.STATUSES_INFO },
    {
      key: TABS.POSSIBLE_STATUSES,
      label: FORM.POSSIBLE_STATUSES.POSSIBLE_STATUSES_INFO,
    },
    { key: TABS.SKILLS, label: FORM.SKILLS.TITLE },
    {
      key: TABS.NEEDS,
      label: FORM.NEEDS_CONFIG || "Configuration des besoins",
    },
    {
      key: TABS.ADDITIONAL_CONFIG,
      label: FORM.ADDITIONAL_CONFIG || "Configuration Additionnel",
    },
  ];

  const handleSave = async () => {
    // Collect values from all form instances
    const generalValues = generalFormRef.current?.values || {};
    const skillsValues = skillsFormRef.current?.values || {};
    const needsValues = needsFormRef.current?.values || {};
    const additionalConfigValues =
      additionalConfigFormRef.current?.values || {};

    // Get status data from both forms:
    // - Statuses tab has the latest status info (name, tag, order, template, etc.)
    // - PossibleStatuses tab has possibleStatuses and actions
    const statusesFormData =
      statusesFormRef.current?.values?.processStatuses || [];
    const possibleStatusesFormData =
      possibleStatusesFormRef.current?.values?.processStatuses || [];

    // Create a map of possibleStatuses/actions by status id from PossibleStatuses form
    const possibleStatusesMap = {};
    possibleStatusesFormData.forEach((status) => {
      possibleStatusesMap[status.id] = {
        possibleStatuses: status.possibleStatuses || [],
        actions: status.actions || [],
      };
    });

    // Merge: take status info from Statuses tab, possibleStatuses/actions from PossibleStatuses tab
    const baseStatuses =
      statusesFormData.length > 0
        ? statusesFormData
        : sharedProcessStatuses.length > 0
          ? sharedProcessStatuses
          : [];

    const processStatusesData = sortProcessStatuses(
      baseStatuses.map((status) => ({
        ...status,
        // Preserve possibleStatuses from PossibleStatuses form, or keep existing
        possibleStatuses:
          possibleStatusesMap[status.id]?.possibleStatuses ||
          status.possibleStatuses ||
          [],
        // Preserve actions from PossibleStatuses form, or keep existing
        actions: possibleStatusesMap[status.id]?.actions || status.actions || [],
      })),
    );

    // Build the merged payload
    const payload = {
      // From General tab
      name: generalValues.name || "",
      type: generalValues.type || PROCESS_TYPES.B2B,
      theme: generalValues.theme || null,
      additionalInfo: {},
      description: generalValues.description || "",

      // From Statuses/PossibleStatuses tabs (merged)
      processStatuses: processStatusesData,

      // From Skills tab
      skills: skillsValues.skills || [],
      attributionType: defaultsDeep(skillsValues.attributionType, {
        id: cuid(),
        type: PROCESS_CONFIG_TYPES.ATTRIBUTION_TYPE,
        value: null,
      }),

      // From Needs tab and Additional Configuration tab
      config: (() => {
        const baseConfig = [];

        // Add attributionType if it has a value
        const attributionTypeConfig = skillsValues.attributionType?.value
          ? [skillsValues.attributionType]
          : [];

        // Add previousAdvisorConfig from Skills tab (always include so it is saved)
        const previousAdvisorConfig = skillsValues.previousAdvisorConfig
          ? [skillsValues.previousAdvisorConfig]
          : [];

        // Build needs config from Needs tab
        let needsConfig = [];

        if (
          needsValues.needsLevels !== undefined ||
          needsValues.selectedCategories?.length > 0
        ) {
          // Build categories object with only selected categories
          const categoriesData = {};
          if (needsValues.selectedCategories?.length > 0) {
            needsValues.selectedCategories.forEach((categoryId) => {
              categoriesData[categoryId] = {};
              // If personalizeText is checked, use the values from form, otherwise empty strings
              if (
                needsValues.personalizeText &&
                needsValues.categories?.[categoryId]
              ) {
                categoriesData[categoryId] = {
                  ...needsValues.categories[categoryId],
                };
              }
              // Ensure all levels are present (only if needsLevels is set)
              if (needsValues.needsLevels) {
                for (let i = 1; i <= needsValues.needsLevels; i++) {
                  if (!categoriesData[categoryId][String(i)]) {
                    categoriesData[categoryId][String(i)] = "";
                  }
                }
              }
            });
          }

          // Build labels object
          const labelsData = {};
          if (needsValues.needsLevels && needsValues.labels) {
            for (let i = 1; i <= needsValues.needsLevels; i++) {
              labelsData[String(i)] = needsValues.labels[String(i)] || "";
            }
          }

          needsConfig = [
            {
              id: cuid(),
              type: PROCESS_CONFIG_TYPES.NEEDS,
              value: {
                needsLevels: needsValues.needsLevels ?? null,
                categories: categoriesData,
                labels: labelsData,
              },
            },
          ];
        }

        // Build product config from Additional Configuration tab
        const productConfig = additionalConfigValues.products?.length
          ? [
              {
                id: cuid(),
                type: PROCESS_CONFIG_TYPES.PRODUCT,
                value: serializeProcessProducts(additionalConfigValues.products),
              },
            ]
          : [];

        const associatedProcessesConfig =
          additionalConfigValues.associatedProcesses?.length > 0
            ? [
                {
                  id: cuid(),
                  type: PROCESS_CONFIG_TYPES.ASSOCIATED_PROCESSES,
                  value: additionalConfigValues.associatedProcessesValue || {
                    processes: [],
                  },
                },
              ]
            : [];

        return [
          ...baseConfig,
          ...attributionTypeConfig,
          ...previousAdvisorConfig,
          ...needsConfig,
          ...productConfig,
          ...associatedProcessesConfig,
        ];
      })(),

      // Other fields
      insurancePlans: [],
    };

    // Submit the merged payload
    await onSubmit(payload);

    // Reset dirty state for all tabs
    setIsDirty({
      [TABS.GENERAL]: false,
      [TABS.STATUSES]: false,
      [TABS.POSSIBLE_STATUSES]: false,
      [TABS.SKILLS]: false,
      [TABS.NEEDS]: false,
      [TABS.ADDITIONAL_CONFIG]: false,
    });
  };

  const isSaveDisabled = !Object.values(isDirty).some(
    (dirty) => dirty === true,
  );

  // Get current values for cross-tab dependencies
  const currentType = generalFormRef.current?.values?.type;
  const currentTheme = generalFormRef.current?.values?.theme;

  return (
    <>
      <Header
        title={CREATE.HEADER.TITLE}
        subtitle={CREATE.HEADER.ACTION}
        actions={[
          {
            text: CREATE.HEADER.DISCARD,
            onClick: cancel,
            type: "primary--outlined",
            icon: <BiChevronLeft size={20} />,
          },
          {
            text: CREATE.HEADER.SAVE,
            onClick: handleSave,
            type: "primary",
            icon: <BiCheck size={20} />,
            disabled: isSaveDisabled,
          },
        ]}
      />
      <Tabs
        variant="admin"
        tabsConfig={tabsConfig}
        activeTab={activeTab}
        onChange={setActiveTab}
      >
        <div style={{ display: activeTab === TABS.GENERAL ? "block" : "none" }}>
          <General
            formRef={generalFormRef}
            dirtyRef={generalDirtyRef}
            setIsDirty={setIsDirty}
            TABS={TABS}
            onSubmit={onSubmit}
          />
        </div>
        <div
          style={{ display: activeTab === TABS.STATUSES ? "block" : "none" }}
        >
          <Statuses
            formRef={statusesFormRef}
            dirtyRef={statusesDirtyRef}
            setIsDirty={setIsDirty}
            TABS={TABS}
            onSubmit={onSubmit}
            type={currentType}
            theme={currentTheme}
            onProcessStatusesChange={setSharedProcessStatuses}
          />
        </div>
        <div
          style={{
            display: activeTab === TABS.POSSIBLE_STATUSES ? "block" : "none",
          }}
        >
          <PossibleStatuses
            formRef={possibleStatusesFormRef}
            dirtyRef={possibleStatusesDirtyRef}
            setIsDirty={setIsDirty}
            TABS={TABS}
            onSubmit={onSubmit}
            processStatuses={sharedProcessStatuses}
            type={currentType}
            theme={currentTheme}
            modal={modal}
            setModal={setModal}
          />
        </div>
        <div style={{ display: activeTab === TABS.SKILLS ? "block" : "none" }}>
          <Skills
            formRef={skillsFormRef}
            dirtyRef={skillsDirtyRef}
            setIsDirty={setIsDirty}
            TABS={TABS}
            onSubmit={onSubmit}
          />
        </div>
        <div style={{ display: activeTab === TABS.NEEDS ? "block" : "none" }}>
          <Needs
            formRef={needsFormRef}
            dirtyRef={needsDirtyRef}
            setIsDirty={setIsDirty}
            TABS={TABS}
            onSubmit={onSubmit}
          />
        </div>
        <div
          style={{
            display: activeTab === TABS.ADDITIONAL_CONFIG ? "block" : "none",
          }}
        >
          <AdditionalConfiguration
            formRef={additionalConfigFormRef}
            dirtyRef={additionalConfigDirtyRef}
            setIsDirty={setIsDirty}
            TABS={TABS}
            onSubmit={onSubmit}
          />
        </div>
      </Tabs>
    </>
  );
};

export default Create;
